import { Box, Divider, IconButton, ListItemText, MenuItem, Select, Typography } from "@material-ui/core";
import { businessCaseOptions } from "apps/platform-client/client/src/models/simulator/options";
import { useTranslation } from "react-i18next";
import { Close } from "@material-ui/icons";
import { ChangeEvent, useEffect, useState } from "react";
import useStyles from "../../styles";
import { LiteBox } from "apps/platform-client/client/src/components/lite/LiteBox";
import { useRecoilValue } from "recoil";
import { snackbarUpdateStatus } from "../../../../store/simulator";

interface IBusinessCaseSelect {
	value: string | null;
	onChange: (newValue: string | null) => void;
	disabled: boolean;
}

export const BusinessCaseSelect = (props: IBusinessCaseSelect) => {
	const { value, disabled, onChange } = props;
	const { t } = useTranslation();
	const [isHovering, setIsHovering] = useState(false);
	const [menuHoveringIndex, setMenuHoveringIndex] = useState<number | null>(null);
	const [isOpen, setOpen] = useState(false);
	const { businessCaseMenu, businessCaseListItem, boxShadowNone, businessCaseClearIcon, w600 } = useStyles();
	const [newValue, setNewValue] = useState<string | null>(value);
	const selectedOption = businessCaseOptions.find((businessCaseOption) => businessCaseOption.value === newValue);
	const businessCaseSnackBarUpdateStatus = useRecoilValue(snackbarUpdateStatus);

	useEffect(() => {
		if (businessCaseSnackBarUpdateStatus.hasError) {
			setNewValue(value);
		}
	}, [businessCaseSnackBarUpdateStatus.hasError, newValue]);

	useEffect(() => {
		return () => {
			setNewValue(null);
		};
	}, []);

	const handleRenderValue = (newValue: unknown) => {
		return newValue ? (
			<Box display="flex" justifyContent="center">
				<Typography variant="body2" className={w600}>
					{t(newValue as string)}
				</Typography>
			</Box>
		) : (
			<Typography variant="body2" className={w600} style={{ marginRight: 6, marginLeft: 6 }}>
				{t("simulation-business-case")}
			</Typography>
		);
	};

	const handleDeselect = (event: React.MouseEvent<HTMLOrSVGElement, MouseEvent>) => {
		event.stopPropagation();
		setNewValue(null);
		onChange(null);
	};

	const handleChange = (
		event: ChangeEvent<{
			value: unknown;
		}>
	) => {
		event.stopPropagation();
		setNewValue(event.target.value as string);
		onChange(event.target.value as string);
		setIsHovering(false);
		setOpen(false);
	};

	const handleClick = (event: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
		event.stopPropagation();
		!disabled && setOpen(!isOpen);
	};

	const handleMouseOver = () => {
		!disabled && setIsHovering(true);
	};

	const handleMouseLeave = () => {
		!disabled && setIsHovering(false);
	};

	const handleMenuMouseOver = (value: number) => {
		setMenuHoveringIndex(value);
	};

	const handleMenuMouseLeave = () => {
		setMenuHoveringIndex(null);
	};

	return (
		<LiteBox width={150} onMouseOver={handleMouseOver} onMouseLeave={handleMouseLeave}>
			{
				<Select
					fullWidth
					style={{
						backgroundColor: selectedOption?.backgroundColor ?? "#f5f8f9",
						color: selectedOption?.textColor ?? "#7ea6b4",
						borderRadius: "5px",
						outline: isHovering && selectedOption ? `1px solid ${selectedOption.textColor}` : "none",
						outlineOffset: "-1px",
						height: 28,
					}}
					SelectDisplayProps={{ style: { paddingRight: 0, marginRight: isHovering ? 16 : 0 } }}
					open={isOpen}
					displayEmpty
					disabled={disabled}
					variant="standard"
					value={newValue}
					onChange={handleChange}
					renderValue={handleRenderValue}
					startAdornment={
						!!newValue &&
						isHovering && (
							<>
								<IconButton onClick={handleDeselect} style={{ padding: 8 }}>
									<Close className={businessCaseClearIcon} />
								</IconButton>
								<Divider style={{ background: selectedOption?.textColor }} orientation="vertical" flexItem />
							</>
						)
					}
					disableUnderline={true}
					MenuProps={{ classes: { list: boxShadowNone } }}
					onFocus={handleMouseLeave}
					onClick={handleClick}
					onClose={handleMouseLeave}
					autoFocus={false}
					{...(selectedOption && !isHovering ? { IconComponent: "image" } : {})}
				>
					{businessCaseOptions.map((option, index) => {
						return (
							<MenuItem
								onMouseOver={() => handleMenuMouseOver(index)}
								onMouseLeave={handleMenuMouseLeave}
								style={{
									backgroundColor: menuHoveringIndex === index ? option.backgroundColorHover : option.backgroundColor,
									color: option.textColor,
								}}
								className={businessCaseMenu}
								key={option.value}
								value={option.value}
							>
								<ListItemText className={businessCaseListItem} primary={t(option.label)} />
							</MenuItem>
						);
					})}
				</Select>
			}
		</LiteBox>
	);
};
